import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, filter } from 'rxjs/operators';
import { DocumentationActions } from './documentation.actions';
import { DocumentationSelectors } from './documentation.selectors';
import { DocumentService } from '@src/app/core/services/document/document.service';
import { RemoteVideoService } from '@src/app/core/services/remote-video/remote-video.service';
import { DocumentMedia, DocumentFile } from '@src/app/core/types/document.type';

@Injectable()
export class DocumentationEffects {
  private actions$ = inject(Actions);
  private documentService = inject(DocumentService);
  private remoteVideoService = inject(RemoteVideoService);
  private store = inject(Store);

  /**
   * Mapea los archivos incluidos a cada documento según su relación field_media_document
   * @param documents Array de documentos
   * @param included Array de archivos incluidos en la respuesta
   * @returns Array de documentos con su archivo asignado
   */
  private mapIncludedFiles(
    documents: DocumentMedia[],
    included?: any[],
  ): DocumentMedia[] {
    if (!included || included.length === 0) {
      return documents;
    }

    return documents.map((doc) => {
      const fileId = doc.relationships?.field_media_document?.data?.id;
      if (fileId) {
        const file = included.find(
          (item) => item.id === fileId,
        ) as DocumentFile;
        if (file) {
          return { ...doc, file };
        }
      }
      return doc;
    });
  }

  // Effect para cargar documentos por término
  loadDocumentsByTerm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentationActions.loadDocumentsByTerm),
      switchMap(({ term }) =>
        this.store
          .select(DocumentationSelectors.selectHasDocumentsByTermFactory(term))
          .pipe(map((hasDocuments) => ({ term, hasDocuments }))),
      ),
      filter(({ hasDocuments, term }) => {
        if (hasDocuments) {
          return false; // No hacer la llamada si ya tenemos los datos
        }
        return true;
      }),
      switchMap(({ term }) =>
        this.documentService.getDocumentsByTerm(term, true).pipe(
          map((response) => {
            const rawDocuments = response.data || [];
            const included = response.included || [];

            // Mapear los archivos incluidos a cada documento
            const documents = this.mapIncludedFiles(rawDocuments, included);

            return DocumentationActions.loadDocumentsByTermSuccess({
              term,
              documents,
            });
          }),
          catchError((error) => {
            return of(
              DocumentationActions.loadDocumentsByTermFailure({ term, error }),
            );
          }),
        ),
      ),
    ),
  );

  // Effect para cargar documento por slug
  loadDocumentBySlug$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentationActions.loadDocumentBySlug),
      switchMap(({ slug }) =>
        this.store
          .select(DocumentationSelectors.selectHasDocumentBySlugFactory(slug))
          .pipe(map((hasDocument) => ({ slug, hasDocument }))),
      ),
      filter(({ hasDocument, slug }) => {
        if (hasDocument) {
          return false; // No hacer la llamada si ya tenemos el documento
        }
        return true;
      }),
      switchMap(({ slug }) =>
        this.documentService.getDocumentBySlug(slug, true).pipe(
          map((response) => {
            const rawDocuments = response.data || [];
            const included = response.included || [];

            // Mapear los archivos incluidos a cada documento
            const documents = this.mapIncludedFiles(rawDocuments, included);

            // Asumimos que el slug es único y tomamos el primero
            const document = documents.length > 0 ? documents[0] : null;

            if (!document) {
              return DocumentationActions.loadDocumentBySlugFailure({
                slug,
                error: { message: 'Document not found' },
              });
            }

            return DocumentationActions.loadDocumentBySlugSuccess({
              slug,
              document,
            });
          }),
          catchError((error) => {
            return of(
              DocumentationActions.loadDocumentBySlugFailure({ slug, error }),
            );
          }),
        ),
      ),
    ),
  );

  // Effect para cargar vídeos remotos por término
  loadRemoteVideosByTerm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentationActions.loadRemoteVideosByTerm),
      switchMap(({ term }) =>
        this.store
          .select(
            DocumentationSelectors.selectHasRemoteVideosByTermFactory(term),
          )
          .pipe(map((hasVideos) => ({ term, hasVideos }))),
      ),
      filter(({ hasVideos }) => {
        if (hasVideos) {
          return false; // No hacer la llamada si ya tenemos los datos
        }
        return true;
      }),
      switchMap(({ term }) =>
        this.remoteVideoService.getDocumentsByTerm(term).pipe(
          map((response) => {
            const videos = response.data || [];

            return DocumentationActions.loadRemoteVideosByTermSuccess({
              term,
              videos,
            });
          }),
          catchError((error) => {
            return of(
              DocumentationActions.loadRemoteVideosByTermFailure({
                term,
                error,
              }),
            );
          }),
        ),
      ),
    ),
  );
}
